import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

type ConsentChoice = 'accepted' | 'refused' | 'custom';

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  social: boolean;
}

const STORAGE_KEY = 'adnova_cookie_consent';
const STORAGE_PREFS_KEY = 'adnova_cookie_prefs';
const CONSENT_EXPIRY_DAYS = 180; // 6 mois CNIL

const isConsentExpired = (): boolean => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return true;
    const { date } = JSON.parse(raw);
    if (!date) return true;
    const diff = Date.now() - new Date(date).getTime();
    return diff > CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return true;
  }
};

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState<CookiePreferences>({
    essential: true,
    analytics: false,
    social: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored || isConsentExpired()) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, []);

  const saveConsent = (choice: ConsentChoice, preferences: CookiePreferences) => {
    setSaving(true);
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ choice, date: new Date().toISOString() })
    );
    localStorage.setItem(STORAGE_PREFS_KEY, JSON.stringify(preferences));
    setTimeout(() => {
      setVisible(false);
      setSaving(false);
    }, 400);
  };

  const handleAcceptAll = () => {
    saveConsent('accepted', { essential: true, analytics: true, social: true });
  };

  const handleRefuseAll = () => {
    saveConsent('refused', { essential: true, analytics: false, social: false });
  };

  const handleSaveCustom = () => {
    saveConsent('custom', prefs);
    setShowDetails(false);
  };

  if (!visible) return null;

  return (
    <>
      {/* Overlay flou si panneau détaillé ouvert */}
      {showDetails && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[998]"
          onClick={() => setShowDetails(false)}
        />
      )}

      {/* Panneau de paramétrage détaillé */}
      {showDetails && (
        <div className="fixed bottom-0 left-0 right-0 z-[999] flex justify-center px-4 pb-4">
          <div className="w-full max-w-lg bg-[#0D1228] border border-white/[0.1] rounded-xl shadow-2xl overflow-hidden">
            {/* Header panneau */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.07]">
              <div className="flex items-center gap-2">
                <i className="ri-settings-3-line text-[#C9A227]"></i>
                <span className="font-bold text-white text-sm">Paramétrer mes cookies</span>
              </div>
              <button
                onClick={() => setShowDetails(false)}
                className="w-7 h-7 flex items-center justify-center text-white/40 hover:text-white/80 transition-colors cursor-pointer rounded-full hover:bg-white/[0.05]"
              >
                <i className="ri-close-line text-base"></i>
              </button>
            </div>

            {/* Liste des catégories */}
            <div className="px-5 py-4 space-y-4">

              {/* Essentiels */}
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-white/90">Cookies essentiels</span>
                    <span className="text-[10px] px-2 py-0.5 bg-[#C9A227]/15 text-[#C9A227] rounded-full border border-[#C9A227]/20 whitespace-nowrap">Toujours actifs</span>
                  </div>
                  <p className="text-xs text-white/40 leading-relaxed">
                    Indispensables au fonctionnement de la plateforme : authentification, sécurité, préférences d&apos;interface. Ne peuvent pas être désactivés.
                  </p>
                </div>
                <div className="shrink-0 mt-1">
                  <div className="w-10 h-5 bg-[#C9A227] rounded-full opacity-60 cursor-not-allowed flex items-center justify-end px-1">
                    <div className="w-3.5 h-3.5 bg-white rounded-full"></div>
                  </div>
                </div>
              </div>

              <div className="border-t border-white/[0.05]"></div>

              {/* Analytics */}
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-semibold text-white/90 block mb-1">Cookies analytics</span>
                  <p className="text-xs text-white/40 leading-relaxed">
                    Mesure d&apos;audience et analyse des comportements de navigation pour améliorer le site. Vos données restent anonymisées.
                  </p>
                </div>
                <button
                  onClick={() => setPrefs(p => ({ ...p, analytics: !p.analytics }))}
                  className={`shrink-0 mt-1 w-10 h-5 rounded-full transition-all duration-200 cursor-pointer flex items-center px-0.5 ${
                    prefs.analytics ? 'bg-[#C9A227] justify-end' : 'bg-white/10 justify-start'
                  }`}
                >
                  <div className="w-4 h-4 bg-white rounded-full shadow transition-all duration-200"></div>
                </button>
              </div>

              <div className="border-t border-white/[0.05]"></div>

              {/* Réseaux sociaux */}
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-semibold text-white/90 block mb-1">Cookies réseaux sociaux</span>
                  <p className="text-xs text-white/40 leading-relaxed">
                    Activés lors de l&apos;interaction avec les boutons de partage (LinkedIn, Instagram…). Soumis aux politiques des plateformes concernées.
                  </p>
                </div>
                <button
                  onClick={() => setPrefs(p => ({ ...p, social: !p.social }))}
                  className={`shrink-0 mt-1 w-10 h-5 rounded-full transition-all duration-200 cursor-pointer flex items-center px-0.5 ${
                    prefs.social ? 'bg-[#C9A227] justify-end' : 'bg-white/10 justify-start'
                  }`}
                >
                  <div className="w-4 h-4 bg-white rounded-full shadow transition-all duration-200"></div>
                </button>
              </div>
            </div>

            {/* Footer panneau */}
            <div className="px-5 py-4 border-t border-white/[0.07] flex items-center justify-between gap-3">
              <Link
                to="/legal/cookies"
                className="text-xs text-white/30 hover:text-[#C9A227] transition-colors cursor-pointer whitespace-nowrap"
                onClick={() => setShowDetails(false)}
              >
                Politique cookies
              </Link>
              <button
                onClick={handleSaveCustom}
                disabled={saving}
                className="px-5 py-2 bg-[#C9A227] text-[#080C1E] text-xs font-bold rounded-lg hover:bg-[#D4B84A] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                Enregistrer mes choix
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bandeau principal */}
      {!showDetails && (
        <div
          className={`fixed bottom-0 left-0 right-0 z-[997] px-4 pb-4 transition-all duration-500 ${
            visible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
          }`}
        >
          <div className="max-w-4xl mx-auto bg-[#0D1228] border border-white/[0.1] rounded-xl overflow-hidden shadow-2xl">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 px-5 py-4">

              {/* Icône + Texte */}
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="w-8 h-8 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-cookie-line text-[#C9A227] text-xl"></i>
                </div>
                <div className="min-w-0">
                  <p className="text-white/90 text-sm font-semibold mb-0.5">
                    Nous utilisons des cookies
                  </p>
                  <p className="text-white/40 text-xs leading-relaxed">
                    Des cookies essentiels assurent le bon fonctionnement du site. Avec votre accord, nous pouvons aussi activer des cookies analytics pour améliorer votre expérience.{' '}
                    <Link
                      to="/legal/cookies"
                      className="text-[#C9A227] hover:underline cursor-pointer"
                    >
                      En savoir plus
                    </Link>
                  </p>
                </div>
              </div>

              {/* Boutons */}
              <div className="flex items-center gap-2 shrink-0 flex-wrap sm:flex-nowrap w-full sm:w-auto">
                <button
                  onClick={() => setShowDetails(true)}
                  className="px-3 py-2 text-xs text-white/50 hover:text-white/80 border border-white/[0.1] hover:border-white/20 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-equalizer-line mr-1"></i>
                  Paramétrer
                </button>
                <button
                  onClick={handleRefuseAll}
                  disabled={saving}
                  className="px-3 py-2 text-xs text-white/50 hover:text-white/80 border border-white/[0.1] hover:border-white/20 rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40"
                >
                  Refuser
                </button>
                <button
                  onClick={handleAcceptAll}
                  disabled={saving}
                  className="px-4 py-2 bg-[#C9A227] text-[#080C1E] text-xs font-bold rounded-lg hover:bg-[#D4B84A] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  Tout accepter
                </button>
              </div>
            </div>

            {/* Barre CNIL */}
            <div className="px-5 py-2 border-t border-white/[0.05] bg-black/20 flex items-center gap-2">
              <i className="ri-shield-check-line text-[#C9A227]/60 text-xs"></i>
              <p className="text-[10px] text-white/20">
                Conforme CNIL — Référentiel cookies du 1er octobre 2020 · Consentement valable 6 mois · AD.NOVA.PRO — lereseausportif.com
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieBanner;
